import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { SWRConfig } from 'swr'

import ErrorBoundary from './components/ErrorBoundary'
import GlobalComponents from './components/GlobalComponents'
import PopupLayer from './components/PopupLayer'
import RootLayout from './components/RootLayout'
import StripeLoader from './components/StripeLoader'
import { ToastProvider } from './components/Toast'
import CheckoutRouter from './pages/CheckoutRouter'
import DefaultRouter from './pages/DefaultRouter'
import LicenseManagement from './pages/LicenseManagement'
import Plans from './pages/Plans'
import Summary from './pages/Summary'
import { EnvFetcher, getPathPrefix } from './services/Env'
import { LocalizationProvider } from './services/Localization'
import useOrganization from './services/useOrganization'
import useWorkspaceId from './services/useWorkspaceId'
import GlobalStyles from './theme/GlobalStyles'
import FacebookPixel from './trackings/FacebookPixel'
import GoogleTag from './trackings/GoogleTag'
import GoogleTagManager from './trackings/GoogleTagManager'
import YahooTag from './trackings/YahooTag'

import 'focus-visible'
import 'normalize.css'

function App() {
  const workspaceId = useWorkspaceId()
  const organization = useOrganization()

  const locale = organization?.userLanguage || 'en'

  useEffect(() => {
    if (organization) {
      if (organization.userAnalyticsEnabled) {
        GoogleTag.initialize()
        FacebookPixel.initialize()
        YahooTag.initialize()
        GoogleTagManager.initialize()
      }
    }
  }, [organization, locale, workspaceId])

  useEffect(() => {
    // Apply local fonts
    document.querySelector('html')!.setAttribute('lang', locale)
  }, [locale])

  return (
    <LocalizationProvider locale={locale}>
      <ToastProvider>
        <StripeLoader>
          <RootLayout>
            <ErrorBoundary>
              <Routes>
                <Route path="/plans" element={<Plans />} />
                <Route path="/plans/upgrade" element={<Plans higherPlansOnly />} />
                <Route path="/checkout" element={<CheckoutRouter />} />
                <Route path="/summary" element={<Summary />} />
                <Route path="/licenses" element={<LicenseManagement />} />
                <Route index element={<DefaultRouter />} />
              </Routes>

              <GlobalComponents />
            </ErrorBoundary>
          </RootLayout>
        </StripeLoader>

        <PopupLayer />
      </ToastProvider>
    </LocalizationProvider>
  )
}

// Set basename (can be null/undefined)
function RouterStarter() {
  const prefix = getPathPrefix()
  return (
    <BrowserRouter basename={prefix}>
      <GlobalStyles />
      <App />
    </BrowserRouter>
  )
}

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <SWRConfig
      value={{
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      }}
    >
      <EnvFetcher>
        <RouterStarter />
      </EnvFetcher>
    </SWRConfig>
  </ErrorBoundary>,
)
